import { useCallback, useMemo } from 'react';
import { RaRecord, useNotify } from 'react-admin';

import { isFullyManagedPlan } from '@Common/types/b2b/plans';
import { PostB2bIndividualOperationVoluntaryDepositBulkBody } from '@Common/types/bo/b2b/individual_operations';

import { isTerminatedContractVoluntaryPaymentError } from '@sh4res/bo-common/components/B2bVoluntaryPayment/voluntaryPaymentCreateError';
import { getErrorMessage } from '@sh4res/bo-common/utils';

type VoluntaryDeposit = PostB2bIndividualOperationVoluntaryDepositBulkBody['investments'][number];

function createHandleTransform(data: RaRecord): PostB2bIndividualOperationVoluntaryDepositBulkBody {
  const investmentsMap = new Map<string, VoluntaryDeposit>();
  const rows = data.rows || [];

  for (const row of rows) {
    if (!row.saving_plan_id || !row.amount) {
      continue;
    }

    const key = `${row.saving_plan_id}-${row.deductibility || 'none'}`;
    const isManaged = row.plan_data ? isFullyManagedPlan(row.plan_data.type) : false;

    if (!investmentsMap.has(key)) {
      const common = {
        b2b_employee_id: data.b2b_employee_id,
        saving_plan_id: row.saving_plan_id,
        payment_method: data.payment_method || 'regular-bank',
        deductibility: row.deductibility || undefined,
      };
      investmentsMap.set(
        key,
        isManaged
          ? { ...common, type: 'fully_managed', amount: row.amount }
          : { ...common, type: 'libre', allocations: [] },
      );
    }

    const op = investmentsMap.get(key);

    // Only `libre` deposits carry per-instrument allocations.
    if (op?.type === 'libre' && row.instrument_id) {
      op.allocations.push({
        instrument_id: row.instrument_id,
        amount: row.amount,
      });
    }
  }

  const investments = Array.from(investmentsMap.values()).filter(
    (op) => op.type !== 'libre' || op.allocations.length > 0,
  );

  return { type: 'voluntary_deposit', investments };
}

export const useVoluntaryPaymentCreate = () => {
  const notify = useNotify();

  const onSuccess = useCallback(() => {
    notify('resources.bo_common.b2b.voluntary_payment_create.actions.success', {
      type: 'info',
    });
  }, [notify]);

  const onError = useCallback(
    (error: unknown) => {
      const detail = getErrorMessage(error);
      if (isTerminatedContractVoluntaryPaymentError(detail)) {
        notify('resources.bo_common.b2b.voluntary_payment_create.actions.restricted', {
          type: 'error',
          multiLine: true,
        });
        return;
      }

      notify('resources.bo_common.b2b.voluntary_payment_create.actions.failure', {
        type: 'error',
        messageArgs: { error: detail },
        multiLine: true,
      });
    },
    [notify],
  );

  return useMemo(
    () => ({ transform: createHandleTransform, mutationOptions: { onSuccess, onError } }),
    [onSuccess, onError],
  );
};
